import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Inertia } from '@inertiajs/inertia';
import { Cart } from '@shopify/app-bridge/actions';
import { DescriptionList, LegacyStack, Modal, Spinner, TextContainer, Text } from '@shopify/polaris';

import Api from '../helpers/api';

import { CartContext } from '../context-providers/CartContext';
import { personalizationIdPropertyName } from '../globals.js';

// Set the line item properties for the CURRENTLY EDITED line item only.
// e.g. you open the engraving data for productLineItemIndex, and that's what comes back here.
export const LineItemPropertiesSetter = ({ productLineItemIndex, engravingProperties, onlyAllowOneEngraving }) => {
    const cartContext = useContext(CartContext);

    const [showingModal, setShowingModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const hideModal = () => {
        setShowingModal(false);
    };

    const getLineItemIndex = () => {
        // Sometimes, when the engraving charge items get added, it means the line item indexes have changed by the time we get here, from when we tapped the item on the CartDisplay.
        // So we index the PRODUCT items (not custom, not engraving charge) and try to match that instead.
        let lineProductIndex = -1;
        for (let lineIndex in cartContext.lineItems) {
            let lineItem = cartContext.lineItems[lineIndex];
            if (lineItem.productId && lineItem.productId != cartContext.engravingChargeProduct.id) {
                lineProductIndex++;
                if (lineProductIndex == productLineItemIndex) {
                    return lineIndex;
                }
            }
        }
    };

    const getRemovalPropertyKeys = () => {
        if (cartContext.productLineItems[productLineItemIndex].properties) {
            return cartContext.productLineItems[productLineItemIndex].properties
                .filter((property) => {
                    return property.name != personalizationIdPropertyName;
                })
                .map((property) => property.name);
        }
        return [];
    };

    const clearOtherEngravings = (personalizationId) => {
        // We can only have one engraving item on the cart.
        // So, we need to delete the personalizationIdPropertyName on any other items.
        // Then the code that removes stale engraving data should take care of the rest.
        cartContext.lineItems.forEach((lineItem, index) => {
            if (
                lineItem.properties &&
                lineItem.properties.find(
                    (property) => property.name == personalizationIdPropertyName && property.value != personalizationId,
                )
            ) {
                cartContext.cart.dispatch(Cart.Action.REMOVE_LINE_ITEM_PROPERTIES, {
                    index: index,
                    data: lineItem.properties.map((property) => property.name),
                });
            }
        });
    };

    const applyLineItemProperties = (personalizationId, engravingProperties) => {
        if (onlyAllowOneEngraving) {
            clearOtherEngravings(personalizationId);
        }

        let propertiesToStore = { ...engravingProperties };

        propertiesToStore[personalizationIdPropertyName] = personalizationId;

        let finishAndReturn = () => {
            window.setTimeout(() => {
                hideModal();
                setSubmitting(false);
                Inertia.visit('/');
            }, 1000);
        };

        if (engravingProperties['Preview'] != undefined) {
            Api.uploadProductImage(engravingProperties['Preview']).then((response) => {
                propertiesToStore['Preview'] = response.data.cdn_image_url;
                delete propertiesToStore['preview_image'];

                cartContext.cart.dispatch(Cart.Action.SET_LINE_ITEM_PROPERTIES, {
                    index: getLineItemIndex(),
                    data: propertiesToStore,
                });
                finishAndReturn();
            });
        } else {
            cartContext.cart.dispatch(Cart.Action.SET_LINE_ITEM_PROPERTIES, {
                index: getLineItemIndex(),
                data: propertiesToStore,
            });
            finishAndReturn();
        }
    };

    const submitData = () => {
        setSubmitting(true);

        let personalizationId = new Date().getTime() + Math.random(); // a quick way to add a unique ID to this line item.
        // This is only needed the first time we see the item, and we won't remove it ever.

        if (cartContext.productLineItems[productLineItemIndex].properties) {
            // Check to see if there's an existing item, and use its personalization id if so
            let existingPersonalizationItem = cartContext.productLineItems[productLineItemIndex].properties.find(
                (property) => {
                    return property.name == personalizationIdPropertyName;
                },
            );
            if (existingPersonalizationItem) {
                personalizationId = existingPersonalizationItem.value;
            }

            // Remove any existing property keys from the product item
            let removePropertyKeys = getRemovalPropertyKeys();

            cartContext.cart.dispatch(Cart.Action.REMOVE_LINE_ITEM_PROPERTIES, {
                index: getLineItemIndex(),
                data: removePropertyKeys,
            });

            // If we needed to remove keys first, wait for that before we add the new ones.
            window.setTimeout(() => {
                applyLineItemProperties(personalizationId, engravingProperties);
            }, 500);
        } else {
            // And apply the ones we received
            applyLineItemProperties(personalizationId, engravingProperties);
        }
    };

    // Show the modal whenever we get new data
    useEffect(() => {
        Sentry.setContext('Engraving properties', { engravingProperties });
        Sentry.captureMessage('Engraving properties updated');
        setShowingModal(Object.keys(engravingProperties).length > 0);
    }, [engravingProperties]);

    return (
        <>
            {showingModal && (
                <Modal
                    open={showingModal}
                    onClose={hideModal}
                    title="Confirm Engraving"
                    primaryAction={{
                        content: 'Confirm',
                        onAction: submitData,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: hideModal,
                        },
                    ]}
                >
                    <Modal.Section>
                        <TextContainer spacing="loose">
                            <DescriptionList
                                items={Object.entries(engravingProperties)
                                    .filter(([key, value]) => {
                                        return key != 'Preview';
                                    })
                                    .map(([key, value]) => {
                                        return { term: key, description: value };
                                    })}
                            />
                            {submitting && (
                                <LegacyStack vertical={false}>
                                    <Spinner accessibilityLabel="Load spinner" size="small" />
                                    <Text variant="bodyLg" as="p">
                                        Storing personalization data...
                                    </Text>
                                </LegacyStack>
                            )}
                        </TextContainer>
                    </Modal.Section>
                </Modal>
            )}
        </>
    );
};
